import { useState } from "react";
import { Dialog, DialogFooter } from "components/ui/Dialog";
import { FaTriangleExclamation, FaCircleQuestion, FaCircleCheck } from "react-icons/fa6";

const TONE_STYLES = {
  danger: { badge: "bg-destructive/10 text-destructive", button: "bg-destructive text-white hover:bg-destructive/90 dark:bg-destructive/60", Icon: FaTriangleExclamation },
  primary: { badge: "bg-primary/10 text-primary", button: "bg-primary text-primary-foreground hover:bg-primary/90", Icon: FaCircleQuestion },
  success: { badge: "bg-yes/15 text-yes", button: "bg-yes text-white hover:bg-yes/90", Icon: FaCircleCheck },
};

export default function ConfirmDialog({
  open,
  icon,
  title,
  description,
  confirmLabel = "Confirm",
  tone = "danger",
  requireReason = false,
  busy = false,
  error,
  onConfirm,
  onCancel,
}) {
  const [reason, setReason] = useState("");

  if (!open) return null;

  const confirmDisabled = busy || (requireReason && reason.trim().length === 0);
  const style = TONE_STYLES[tone] || TONE_STYLES.danger;
  const Icon = icon || style.Icon;

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} dismissible={!busy} role="alertdialog" showClose={!busy} label={title}>
      <div className="flex items-start gap-4 pr-8">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg ${style.badge}`}>
          <Icon />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold leading-tight text-foreground">{title}</h2>
          {description && <p className="mt-2 text-sm text-muted-foreground">{description}</p>}
        </div>
      </div>

      {requireReason && (
        <div>
          <label htmlFor="confirm-dialog-reason" className="text-sm font-medium text-foreground">Reason*</label>
          <textarea
            id="confirm-dialog-reason"
            className="mt-2 w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm text-foreground outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus:border-ring focus:ring-[3px] focus:ring-ring/50 dark:bg-input/30"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Required for the audit record…"
            autoFocus
          />
        </div>
      )}

      {error && <p className="text-sm font-medium text-destructive">{error}</p>}

      <DialogFooter>
        <button
          onClick={onCancel}
          disabled={busy}
          className="inline-flex h-9 items-center justify-center rounded-sm border border-border bg-background px-4 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50 dark:bg-input/30 dark:hover:bg-input/50"
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(reason)}
          disabled={confirmDisabled}
          className={`inline-flex h-9 items-center justify-center rounded-sm px-4 text-sm font-medium transition-colors disabled:opacity-50 ${style.button}`}
        >
          {busy ? "Working…" : confirmLabel}
        </button>
      </DialogFooter>
    </Dialog>
  );
}
